<script setup lang="ts">
import { useI18n } from '@/i18n'
import { useAuthStore } from '@/stores/auth'
import { computed, ref } from 'vue'

const { t } = useI18n()
const authStore = useAuthStore()
const isAuthenticated = computed(() => authStore.isAuthenticated)

const features = [
  { icon: '🎵', key: 'upload' },
  { icon: '🤖', key: 'ai' },
  { icon: '🎬', key: 'video' }
]

const whyItems = [
  { icon: '⚡', key: 'fast' },
  { icon: '🎥', key: 'quality' },
  { icon: '🧠', key: 'ai' },
  { icon: '📐', key: 'formats' }
]

// Demo video state
const isPlaying = ref(false)
const videoRef = ref<HTMLVideoElement | null>(null)

function playDemo() {
  isPlaying.value = true
  // Wait for next tick to ensure video element is rendered
  setTimeout(() => {
    if (videoRef.value) {
      videoRef.value.play()
    }
  }, 100)
}

function onVideoEnded() {
  isPlaying.value = false
}

function onVideoPause() {
  // Optional: return to poster when paused
  // isPlaying.value = false
}
</script>

<template>
  <div class="home-view">
    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-bg">
        <div class="hero-glow"></div>
        <div class="hero-grid"></div>
      </div>
      
      <div class="hero-content animate-slideUp">
        <h1 class="hero-title">
          {{ t('home.hero.title') }}
        </h1>
        <p class="hero-subtitle">
          {{ t('home.hero.subtitle') }}
        </p>
        
        <div class="hero-actions">
          <router-link 
            :to="isAuthenticated ? '/dashboard' : '/login'" 
            class="btn-primary"
          >
            {{ t('home.hero.cta') }}
            <span class="btn-arrow">→</span>
          </router-link>
          <router-link to="/gallery" class="btn-secondary">
            {{ t('home.hero.watch') }}
          </router-link>
        </div>
        
        <!-- Demo Video Preview -->
        <div class="hero-preview">
          <div class="preview-frame">
            <!-- Poster image with play button (shown when not playing) -->
            <div v-if="!isPlaying" class="preview-poster" @click="playDemo">
              <img 
                src="/demo_m2v.io.png" 
                alt="M2V.io Demo Preview" 
                class="preview-image"
              />
              <div class="preview-overlay">
                <div class="preview-play">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
                <span class="preview-label">{{ t('home.demo.watch') }}</span>
              </div>
            </div>
            
            <!-- Video player (shown when playing) -->
            <video 
              v-show="isPlaying"
              ref="videoRef"
              class="preview-video"
              controls
              @ended="onVideoEnded"
              @pause="onVideoPause"
            >
              <source src="/demo_m2v.io.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </div>
    </section>
    
    <!-- Features Section -->
    <section class="features">
      <div class="container">
        <h2 class="section-title">{{ t('home.features.title') }}</h2>
        
        <div class="features-grid">
          <div 
            v-for="(feature, index) in features" 
            :key="feature.key"
            class="feature-card"
            :style="{ animationDelay: `${index * 100}ms` }"
          >
            <div class="feature-icon">{{ feature.icon }}</div>
            <div class="feature-step">{{ index + 1 }}</div>
            <h3 class="feature-title">{{ t(`home.features.${feature.key}.title`) }}</h3>
            <p class="feature-desc">{{ t(`home.features.${feature.key}.desc`) }}</p>
          </div>
        </div>
      </div>
    </section>
    
    <!-- Why Section -->
    <section class="why">
      <div class="container">
        <h2 class="section-title">{{ t('home.why.title') }}</h2>
        
        <div class="why-grid">
          <div 
            v-for="item in whyItems" 
            :key="item.key"
            class="why-card"
          >
            <div class="why-icon">{{ item.icon }}</div>
            <h3 class="why-title">{{ t(`home.why.${item.key}`) }}</h3>
            <p class="why-desc">{{ t(`home.why.${item.key}.desc`) }}</p>
          </div>
        </div>
      </div>
    </section>
    
    <!-- CTA Section -->
    <section class="cta">
      <div class="container">
        <div class="cta-card">
          <h2>{{ t('home.cta.title') }}</h2>
          <p>{{ t('home.cta.subtitle') }}</p>
          <router-link 
            :to="isAuthenticated ? '/dashboard' : '/login'" 
            class="btn-primary large"
          >
            {{ t('home.hero.cta') }}
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.home-view {
  overflow-x: hidden;
}

/* Hero */
.hero {
  position: relative;
  min-height: calc(100vh - 64px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--m2v-space-12) var(--m2v-space-6);
}

.hero-bg {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.hero-glow {
  position: absolute;
  top: -50%;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 1000px;
  height: 100%;
  background: var(--m2v-gradient-glow);
  filter: blur(100px);
  opacity: 0.6;
}

.hero-grid {
  position: absolute;
  inset: 0;
  background-image: 
    linear-gradient(var(--m2v-border-subtle) 1px, transparent 1px),
    linear-gradient(90deg, var(--m2v-border-subtle) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse at center, black 30%, transparent 70%);
}

.hero-content {
  position: relative;
  text-align: center;
  max-width: 900px;
}

.hero-title {
  font-family: var(--m2v-font-display);
  font-size: clamp(2.5rem, 8vw, 4.5rem);
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: var(--m2v-space-6);
  background: var(--m2v-gradient-hero);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: clamp(1.125rem, 2vw, 1.375rem);
  color: var(--m2v-text-secondary);
  max-width: 600px;
  margin: 0 auto var(--m2v-space-8);
  line-height: 1.6;
}

.hero-actions {
  display: flex;
  gap: var(--m2v-space-4);
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: var(--m2v-space-12);
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: var(--m2v-space-2);
  padding: var(--m2v-space-4) var(--m2v-space-8);
  background: var(--m2v-gradient-hero);
  color: white;
  font-weight: 600;
  font-size: 1.125rem;
  border-radius: var(--m2v-radius-lg);
  transition: all var(--m2v-transition-normal);
  box-shadow: 0 4px 20px rgba(99, 102, 241, 0.4);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 30px rgba(99, 102, 241, 0.5);
  color: white;
}

.btn-primary.large {
  padding: var(--m2v-space-5) var(--m2v-space-10);
  font-size: 1.25rem;
}

.btn-arrow {
  transition: transform var(--m2v-transition-fast);
}

.btn-primary:hover .btn-arrow {
  transform: translateX(4px);
}

.btn-secondary {
  padding: var(--m2v-space-4) var(--m2v-space-8);
  background: transparent;
  color: var(--m2v-text-primary);
  font-weight: 600;
  font-size: 1.125rem;
  border: 1px solid var(--m2v-border-default);
  border-radius: var(--m2v-radius-lg);
  transition: all var(--m2v-transition-normal);
}

.btn-secondary:hover {
  background: var(--m2v-border-subtle);
  border-color: var(--m2v-border-emphasis);
}

.hero-preview {
  max-width: 800px;
  margin: 0 auto;
}

.preview-frame {
  aspect-ratio: 16/9;
  background: var(--m2v-bg-elevated);
  border: 1px solid var(--m2v-border-default);
  border-radius: var(--m2v-radius-xl);
  overflow: hidden;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.preview-poster {
  position: absolute;
  inset: 0;
  cursor: pointer;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--m2v-space-4);
  background: rgba(0, 0, 0, 0.4);
  transition: all var(--m2v-transition-normal);
}

.preview-poster:hover .preview-overlay {
  background: rgba(0, 0, 0, 0.3);
}

.preview-play {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--m2v-primary);
  border-radius: var(--m2v-radius-full);
  color: white;
  cursor: pointer;
  transition: all var(--m2v-transition-normal);
  box-shadow: 0 4px 20px rgba(99, 102, 241, 0.4);
}

.preview-poster:hover .preview-play {
  transform: scale(1.1);
  box-shadow: 0 0 40px rgba(99, 102, 241, 0.6);
}

.preview-label {
  color: white;
  font-size: 1.125rem;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.preview-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: black;
}

/* Features */
.features {
  padding: var(--m2v-space-12) var(--m2v-space-6);
  background: var(--m2v-bg-surface);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

.section-title {
  font-family: var(--m2v-font-display);
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: var(--m2v-space-12);
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--m2v-space-8);
}

.feature-card {
  position: relative;
  padding: var(--m2v-space-8);
  background: var(--m2v-bg-elevated);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-xl);
  text-align: center;
  animation: slideUp var(--m2v-transition-slow) ease-out backwards;
}

.feature-card:hover {
  border-color: var(--m2v-primary);
  box-shadow: 0 0 30px rgba(99, 102, 241, 0.1);
}

.feature-icon {
  font-size: 3rem;
  margin-bottom: var(--m2v-space-4);
}

.feature-step {
  position: absolute;
  top: var(--m2v-space-4);
  right: var(--m2v-space-4);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--m2v-primary);
  color: white;
  font-weight: 700;
  border-radius: var(--m2v-radius-full);
}

.feature-title {
  font-size: 1.25rem;
  margin-bottom: var(--m2v-space-3);
}

.feature-desc {
  color: var(--m2v-text-muted);
}

/* Why Section */
.why {
  padding: var(--m2v-space-12) var(--m2v-space-6);
}

.why-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--m2v-space-6);
}

.why-card {
  padding: var(--m2v-space-6);
  background: var(--m2v-bg-surface);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-lg);
  text-align: center;
  transition: all var(--m2v-transition-normal);
}

.why-card:hover {
  transform: translateY(-4px);
  border-color: var(--m2v-primary);
}

.why-icon {
  font-size: 2.5rem;
  margin-bottom: var(--m2v-space-3);
}

.why-title {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: var(--m2v-space-2);
}

.why-desc {
  color: var(--m2v-text-muted);
  font-size: 0.9rem;
}

/* CTA */
.cta {
  padding: var(--m2v-space-12) var(--m2v-space-6);
}

.cta-card {
  text-align: center;
  padding: var(--m2v-space-12);
  background: var(--m2v-gradient-card);
  border: 1px solid var(--m2v-border-default);
  border-radius: var(--m2v-radius-xl);
}

.cta-card h2 {
  font-size: 2rem;
  margin-bottom: var(--m2v-space-4);
}

.cta-card p {
  color: var(--m2v-text-muted);
  margin-bottom: var(--m2v-space-8);
}
</style>
